import { useMemo } from 'react';

import {
  FetchProductsSetGqlQuery,
  ProductsSetUUID
} from '../../productsSetsTypes';

import { FetchProductsSetCacheKey } from '../../productsSetsTypes';

import { useShowQuery } from '../../../common/reactQuery/hooks/useShowQuery';

interface ProductsSetResponse<ItemType> {
  productsSet: ItemType | null;
}

interface ProductsSetOptions<ItemType> {
  productsSets?: ItemType[];
  cacheKey: FetchProductsSetCacheKey;
  query: FetchProductsSetGqlQuery;
  uuid: ProductsSetUUID;
  options?: {
    cacheTime?: number;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
  };
}

interface UseProductsSetItem {
  uuid: ProductsSetUUID;
}

const itemKey = 'productsSet';

function useProductsSet<ItemType extends UseProductsSetItem>({
  productsSets,
  cacheKey,
  query,
  uuid,
  options = {}
}: ProductsSetOptions<ItemType>) {
  const placeholderData = useMemo<ProductsSetResponse<ItemType>>(() => {
    const productsSet = productsSets?.find(
      (productsSet) => productsSet.uuid === uuid
    );

    return productsSet ? { productsSet } : null;
  }, [productsSets, uuid]);

  const {
    item,
    itemError,
    itemErrorData,
    itemFetched,
    itemFetching,
    itemLoading,
    itemIsPlaceholderData,
    refetchItem,
    updateItemCache
  } = useShowQuery<ProductsSetResponse<ItemType>, ItemType>({
    cacheKey,
    itemKey,
    options,
    placeholderData,
    query,
    uuid
  });

  return {
    productsSet: item,
    productsSetError: itemError,
    productsSetErrorData: itemErrorData,
    productsSetFetched: itemFetched,
    productsSetFetching: itemFetching,
    productsSetIsPlaceholderData: itemIsPlaceholderData,
    productsSetLoading: itemLoading,
    productsSetUpdateCache: updateItemCache,
    refetchProductsSet: refetchItem
  };
}

export default useProductsSet;
